// FirstProcessingUploaderChunk.ts - Optimized class for chunked upload of First Processing data
import { getApiUrl } from '@/config/environment';
import {
  getAuthToken,
  translateMessage,
  createSocketManager,
  validateCSVFormat,
  validateCSVSecurity,
  cleanCSVContent,
  ensureHeaderRow,
  splitContentIntoChunks,
  encodeChunk,
  calculateBackoff,
  waitForCompletion,
  CHUNK_SIZES,
  type SocketManager
} from '@/core/services/upload/utils';

/**
 * Interface for upload progress tracking
 * Dva odvojena progress bara:
 * - uploadProgress: Frontend kontrolira (chunk upload)
 * - processingProgress: Backend kontrolira (CSV obrada)
 */
export interface FirstProcessingUploadProgress {
  uploadProgress: number;      // 0-100% - Frontend chunk upload
  processingProgress: number;  // 0-100% - Backend processing
  status: 'uploading' | 'processing' | 'completed' | 'error';
  error?: string;
  message?: string;
  step?: string;               // Backend step: chunk_assembly, parsing, preprocessing, processing, streaming, complete
  uploadId?: string;
  eta?: number;                // Estimated time remaining in seconds
  etaFormatted?: string;       // Formatted ETA string (e.g., "2m 30s")
  currentStep?: number;        // Current processing step (from backend)
  totalSteps?: number;         // Total processing steps (from backend)
}

/**
 * Interface for chunk metadata
 */
export interface FirstProcessingChunkMetadata {
  uploadId: string;
  chunkIndex: number;
  totalChunks: number;
  chunkSize: number;
  totalSize: number;
}

/**
 * Interface for First Processing parameters
 */
export interface FirstProcessingParams {
  tss: number;
  offset: number;
  mode: string;
  intrplMax?: number;
  decimalPrecision?: number | 'full';
  anchorTime?: string;
}

/**
 * Interface for server response
 */
export interface FirstProcessingServerResponse {
  success?: boolean;
  error?: string;
  message?: string;
  data?: Record<string, unknown>[];
}

/**
 * Optimized class for chunked upload of First Processing data
 * Uses shared utilities for socket management, CSV validation, and retry logic
 */
export class FirstProcessingUploaderChunk {
  // Configuration constants
  private static readonly CHUNK_SIZE = CHUNK_SIZES.MEDIUM; // 5MB chunks
  private static readonly MAX_RETRIES = 3;
  private static readonly RETRY_BASE_DELAY = 2000;
  private static readonly REQUEST_TIMEOUT = 600000; // 10 minutes

  private static get API_BASE_URL(): string {
    return getApiUrl('/api/firstProcessing');
  }

  // Socket manager instance
  private static socketManager: SocketManager | null = null;
  private static highestProgress: number = 0;
  private static socketCompletionReceived: boolean = false;

  /**
   * Get or create socket manager instance
   */
  private static getSocketManager(): SocketManager {
    if (!this.socketManager) {
      this.socketManager = createSocketManager({
        timeout: this.REQUEST_TIMEOUT,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5
      });
    }
    return this.socketManager;
  }

  /**
   * Close Socket.IO connection
   */
  public static disconnectSocket(): void {
    if (this.socketManager) {
      this.socketManager.disconnect();
      this.socketManager = null;
    }
  }

  /**
   * Generate unique upload ID
   */
  private static generateUploadId(): string {
    return `fp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Upload single chunk with retry logic
   */
  private static async uploadChunk(
    chunk: Uint8Array,
    metadata: FirstProcessingChunkMetadata,
    params: FirstProcessingParams
  ): Promise<Response> {
    const formData = new FormData();

    // Add chunk as blob
    const chunkBlob = new Blob([chunk.buffer as ArrayBuffer]);
    formData.append("fileChunk", chunkBlob);

    // Add chunk metadata
    formData.append("uploadId", metadata.uploadId);
    formData.append("chunkIndex", metadata.chunkIndex.toString());
    formData.append("totalChunks", metadata.totalChunks.toString());

    // Add First Processing parameters
    formData.append("tss", params.tss.toString());
    formData.append("offset", params.offset.toString());
    formData.append("mode", params.mode);
    formData.append(
      "intrplMax",
      params.mode === "intrpl" ? (params.intrplMax || 60).toString() : "60"
    );
    formData.append(
      "decimalPrecision",
      params.decimalPrecision?.toString() || "full"
    );

    if (params.anchorTime) {
      formData.append("anchorTime", params.anchorTime);
    }

    // Get authentication token
    const accessToken = await getAuthToken();

    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.REQUEST_TIMEOUT);

    try {
      const response = await fetch(`${this.API_BASE_URL}/upload_chunk`, {
        method: "POST",
        body: formData,
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        // Try to extract the backend's JSON error body. Falls back to
        // generic status text if parsing fails (e.g. HTML error page from
        // a load balancer). Attach the HTTP status so the retry loop can
        // refuse to retry 4xx client errors.
        // Also captures structured error_code and suggestions[0] from the
        // BE killshot contract ({error, error_code, suggestions}) so that
        // downstream code (C3/C4) can surface i18n-aware messages.
        let backendErr: string | undefined;
        let errorCode: string | undefined;
        let suggestion: string | undefined;
        try {
          const body = await response.clone().json();
          backendErr = typeof body?.error === 'string' ? body.error : undefined;
          if (typeof body?.error_code === 'string') errorCode = body.error_code;
          if (Array.isArray(body?.suggestions) && typeof body.suggestions[0] === 'string') {
            suggestion = body.suggestions[0];
          }
        } catch {
          // body wasn't JSON — keep backendErr undefined
        }
        const err = new Error(
          backendErr || `Chunk upload failed: ${response.status} ${response.statusText}`
        ) as Error & { status?: number; code?: string; suggestion?: string };
        err.status = response.status;
        if (errorCode) err.code = errorCode;
        if (suggestion) err.suggestion = suggestion;
        throw err;
      }

      return response;
    } catch (error: unknown) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout - server not responding');
      }
      throw error;
    }
  }

  /**
   * Upload chunks sequentially for Cloud Run compatibility
   * Sequential uploads ensure Session Affinity routes all chunks to same instance
   */
  private static async uploadChunksSequential(
    chunks: string[],
    uploadId: string,
    params: FirstProcessingParams,
    cleanedContent: string,
    onProgress?: (progress: FirstProcessingUploadProgress) => void
  ): Promise<Response> {
    const totalChunks = chunks.length;
    let finalResponse: Response | undefined;
    const contentSize = encodeChunk(cleanedContent).length;

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const chunkContent = chunks[chunkIndex];
      const chunkBytes = encodeChunk(chunkContent);

      const metadata: FirstProcessingChunkMetadata = {
        uploadId,
        chunkIndex,
        totalChunks,
        chunkSize: chunkBytes.length,
        totalSize: contentSize
      };

      // Retry logic for each chunk
      let retryCount = 0;
      let lastError: Error | unknown;

      while (retryCount <= this.MAX_RETRIES) {
        try {
          finalResponse = await this.uploadChunk(chunkBytes, metadata, params);
          break; // Success, exit retry loop
        } catch (chunkError) {
          lastError = chunkError;
          // Do NOT retry 4xx client errors. They mean the payload is
          // invalid (e.g. wrong timestamp format, missing column);
          // retrying changes nothing and only burns user-visible time.
          // Surface the backend's message immediately.
          const status = (chunkError as { status?: number }).status;
          if (status !== undefined && status >= 400 && status < 500) {
            throw chunkError;
          }
          retryCount++;

          if (retryCount <= this.MAX_RETRIES) {
            const delay = calculateBackoff(retryCount, this.RETRY_BASE_DELAY);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }

      // If all retries failed, throw
      if (retryCount > this.MAX_RETRIES) {
        throw lastError;
      }

      // Update upload progress
      const currentUploadProgress = Math.round(((chunkIndex + 1) / totalChunks) * 100);
      onProgress?.({
        uploadProgress: currentUploadProgress,
        processingProgress: 0,
        status: 'uploading',
        message: `Uploading chunk ${chunkIndex + 1}/${totalChunks}...`,
        uploadId
      });
    }

    if (!finalResponse) {
      throw new Error('No response received from server');
    }

    return finalResponse;
  }

  /**
   * Process streaming response from backend
   */
  private static async processStreamingResponse(
    response: Response
  ): Promise<Record<string, unknown>[]> {
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Response not readable');
    }

    const decoder = new TextDecoder();
    let buffer = "";
    const accumulatedData: Record<string, unknown>[] = [];

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const decodedChunk = decoder.decode(value, { stream: true });
        buffer += decodedChunk;
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line) continue;

          try {
            const trimmedLine = line.trim();
            if (!trimmedLine || trimmedLine.includes("<Response streamed")) continue;

            if (trimmedLine.startsWith("{") && trimmedLine.endsWith("}")) {
              const chunk = JSON.parse(trimmedLine);

              if (chunk.UTC) {
                accumulatedData.push(chunk);
              } else if (chunk.status === "complete") {
                if (accumulatedData.length === 0) {
                  throw new Error('No data received from server');
                }
                return accumulatedData;
              } else if (chunk.error) {
                throw new Error(chunk.error);
              }
            }
          } catch {
            // Continue - some lines might not be valid JSON
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    if (accumulatedData.length > 0) {
      return accumulatedData;
    }

    throw new Error('No data received from server');
  }

  /**
   * Main method for file upload with parameters
   */
  public static async uploadFile(
    fileContent: string,
    params: FirstProcessingParams,
    onProgress?: (progress: FirstProcessingUploadProgress) => void
  ): Promise<FirstProcessingServerResponse> {
    let uploadId: string = '';
    const socketManager = this.getSocketManager();
    this.socketCompletionReceived = false;
    this.highestProgress = 0;

    // Progress handler for Socket.IO events
    const progressHandler = (data: Record<string, unknown>) => {
      if (data.uploadId !== uploadId || !onProgress) return;

      const step = String(data.step || '');
      const status = step === 'complete' ? 'completed' : (step === 'error' ? 'error' : 'processing');

      if (step === 'complete' || step === 'completed') {
        this.socketCompletionReceived = true;
      }

      const rawProgress = Math.min(Math.max(Number(data.progress) || 0, 0), 100);

      if (status !== 'error' && rawProgress < this.highestProgress) {
        return;
      }

      if (rawProgress > this.highestProgress) {
        this.highestProgress = rawProgress;
      }

      const translatedMessage = data.messageKey
        ? translateMessage(
            data.messageKey as string,
            data.messageParams as Record<string, unknown> | undefined,
            data.message as string | undefined
          )
        : String(data.message || '');

      onProgress({
        uploadProgress: 100,
        processingProgress: rawProgress,
        status: status,
        message: translatedMessage,
        step: step,
        eta: data.eta ? Number(data.eta) : undefined,
        etaFormatted: data.etaFormatted ? String(data.etaFormatted) : undefined,
        currentStep: data.currentStep ? Number(data.currentStep) : undefined,
        totalSteps: data.totalSteps ? Number(data.totalSteps) : undefined
      });
    };

    try {
      // Validate parameters
      if (params.tss === undefined || params.tss === null ||
          params.offset === undefined || params.offset === null ||
          !fileContent) {
        throw new Error('All fields are required');
      }

      if (params.tss < 0 || params.offset < 0) {
        throw new Error('TSS and Offset must be non-negative numbers');
      }

      // Security validation BEFORE any processing
      const securityResult = validateCSVSecurity(fileContent);
      if (!securityResult.isValid) {
        throw new Error(`Security validation failed: ${securityResult.error}`);
      }

      // Ensure CSV has header row and validate format
      const contentWithHeader = ensureHeaderRow(fileContent);
      const validation = validateCSVFormat(contentWithHeader);
      if (!validation.isValid) {
        throw new Error(`CSV validation failed: ${validation.error}`);
      }

      // Clean CSV content
      let cleanedContent = cleanCSVContent(contentWithHeader);
      if (!cleanedContent.startsWith('UTC;')) {
        cleanedContent = `UTC;Leistung [kW]\n${cleanedContent}`;
      }

      // Generate upload ID and setup socket
      uploadId = this.generateUploadId();
      socketManager.joinRoom(uploadId);
      socketManager.onProgress(progressHandler);

      // Split content into chunks
      const chunks = splitContentIntoChunks(cleanedContent, this.CHUNK_SIZE);

      if (chunks.length === 0) {
        throw new Error('Empty file');
      }

      onProgress?.({
        uploadProgress: 0,
        processingProgress: 0,
        status: 'uploading',
        message: 'Starting upload...',
        uploadId
      });

      // Upload chunks sequentially for Cloud Run Session Affinity compatibility
      const finalResponse = await this.uploadChunksSequential(
        chunks,
        uploadId,
        params,
        cleanedContent,
        onProgress
      );

      if (!finalResponse) {
        throw new Error('No response received from server');
      }

      // Process streaming response
      const responseData = await this.processStreamingResponse(finalResponse);

      // Wait for Socket.IO completion
      await waitForCompletion(() => this.socketCompletionReceived, 120000);

      // Cleanup
      socketManager.offProgress(progressHandler);
      socketManager.leaveRoom(uploadId);

      return {
        success: true,
        data: responseData,
        message: 'File processed successfully'
      };

    } catch (error) {
      await waitForCompletion(() => this.socketCompletionReceived, 30000);

      socketManager.offProgress(progressHandler);
      if (uploadId) {
        socketManager.leaveRoom(uploadId);
      }

      let errorMessage = 'Connection error';

      if (typeof window !== 'undefined' && !window.navigator.onLine) {
        errorMessage = 'No internet connection';
      } else if (error instanceof TypeError && error.message === "Failed to fetch") {
        errorMessage = 'Server not reachable';
      } else {
        errorMessage = `Unexpected error: ${error instanceof Error ? error.message : 'Unknown error'}`;
      }

      onProgress?.({
        uploadProgress: 0,
        processingProgress: 0,
        status: 'error',
        error: errorMessage,
        uploadId: uploadId || 'unknown'
      });

      return { error: errorMessage };
    }
  }
}

export default FirstProcessingUploaderChunk;
